import { useEffect } from 'react';
import Cookies from 'js-cookie';
import { SUPPORTED_TIMEZONE_IDS_SET } from '@rocco/ui/calendar/constants';

const TZ_COOKIE_NAME = 'tz-session-booking';

/** Re-export for backward compatibility; use SUPPORTED_TIMEZONE_IDS_SET from @rocco for new code */
export const VALID_TIMEZONES = SUPPORTED_TIMEZONE_IDS_SET;

export function getTimezoneFromUrlParam(request: Request): string | undefined {
    const url = new URL(request.url);
    const tzParam = url.searchParams.get('tz')?.trim();
    return tzParam && SUPPORTED_TIMEZONE_IDS_SET.has(tzParam)
        ? tzParam
        : undefined;
}

/**
 * Check if a string is a valid IANA timezone identifier.
 * Rejects malformed or invalid values that would throw in dayjs.tz() or Intl.
 */
function isValidIANATimezone(tz: string): boolean {
    if (!tz || typeof tz !== 'string' || !tz.includes('/')) return false;
    try {
        Intl.DateTimeFormat(undefined, { timeZone: tz });
        return true;
    } catch {
        return false;
    }
}

/**
 * Get the timezone cookie value from the request headers.
 * Returns undefined if the cookie is missing or contains an invalid IANA timezone.
 */
export function getTimezoneCookieFromRequest(
    request: Request,
): string | undefined {
    const cookieHeader = request.headers.get('cookie');
    const raw = cookieHeader
        ?.split(';')
        .find((cookie) => cookie.trim().startsWith(`${TZ_COOKIE_NAME}=`))
        ?.split('=')[1]
        ?.trim();
    return raw && isValidIANATimezone(raw) ? raw : undefined;
}

/**
 * Check if a timezone is in the supported list.
 */
export function isTimezoneSupported(tz: string): boolean {
    return SUPPORTED_TIMEZONE_IDS_SET.has(tz);
}

function setTimezoneCookie(timezone: string): void {
    if (typeof window === 'undefined') return;
    Cookies.set(TZ_COOKIE_NAME, timezone, {
        path: '/',
        expires: 365,
        sameSite: 'lax',
    });
}

export function useTimezoneSync(currentTimezone: string): void {
    useEffect(() => {
        setTimezoneCookie(currentTimezone);
    }, [currentTimezone]);
}
